import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { faClose, faUserPen } from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { ResellerProfile } from "../../hooks/Reseller/useGetResellerProfile"
import { useUpdateResellerProfile } from "../../hooks/Reseller/useUpdateResellerProfile"

interface EditResellerProfilePopupProps {
  profile: ResellerProfile
  onClose: () => void
  onSuccess: () => void
}

export const EditResellerProfilePopup: React.FC<EditResellerProfilePopupProps> = ({ profile, onClose, onSuccess }) => {
  const { t } = useTranslation("reseller")

  const [name, setName] = useState(profile.name)

  const [phoneNumber, setPhoneNumber] = useState(profile.phoneNumber ?? "")

  const [documentCpfCnpj, setDocumentCpfCnpj] = useState(profile.documentCpfCnpj)

  const { handleUpdateResellerProfile } = useUpdateResellerProfile()

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    handleUpdateResellerProfile({ name, phoneNumber, documentCpfCnpj }, () => {
      onSuccess()
      onClose()
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("editProfile.title")}
              icon={faUserPen}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.reseller.editresellerprofilepopup.1"
                    variant="global"
                    title={t("helpPopup:global.resellerEditProfile.title")}
                    description={t("helpPopup:global.resellerEditProfile.description")}
                    bullets={t("helpPopup:global.resellerEditProfile.bullets", { returnObjects: true }) as string[]}
                    steps={t("helpPopup:global.resellerEditProfile.steps", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    descriptionKey="interactionDescriptions:pages.reseller.editProfilePopup.button.onClose"
                  />
                </div>
              }
            />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 p-2 mt-4">
                  <InputField
                    value={name}
                    type="text"
                    placeholder={t("editProfile.label_name_placeholder")}
                    onChange={(e) => setName(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.reseller.editProfilePopup.input.name"
                  />

                  <InputField
                    value={phoneNumber}
                    type="text"
                    placeholder={t("editProfile.label_phone_placeholder")}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.reseller.editProfilePopup.input.phoneNumber"
                  />

                  <InputField
                    value={documentCpfCnpj}
                    type="text"
                    placeholder={t("editProfile.label_document_placeholder")}
                    onChange={(e) => setDocumentCpfCnpj(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.reseller.editProfilePopup.input.document"
                  />
                </div>

                <div className={defaultStyle.divisionBar}></div>

                <div className={defaultStyle.buttonsController}>
                  <Button
                    typeButton="submit"
                    label={t("editProfile.button_save")}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.reseller.editProfilePopup.button.save"
                  />
                </div>
              </form>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
